import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';

import { useFormContext } from '@/components/react-hook-form';
import { DispositiveProvisionsFormRecipient } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { isNonTaxable } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/hooks/useDefaultBearsTax';
import { PathsOf } from '@/types/subform';

import {
  BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE,
  BeneficiaryDesignationsFormShape,
} from '../BeneficiaryDesignationsForm.types';

/**
 * BDG analog of the DP form's `useDefaultBearsTax`: defaults a beneficiary's
 * `bearsTax` from its recipient type — taxable dispositions bear tax, non-taxable
 * (charitable / marital deduction) ones do not.
 *
 * Like DP, this defaults `bearsTax` once, only when it is unset; it never
 * overrides a value already set by a saved record, a manual toggle, or the
 * residuary toggle. (Re-defaulting on later type changes would mistake the
 * residuary toggle's writes for user input — see PR #10909 review.)
 *
 * Reads the recipient via an array-watch indexed by position rather than the
 * DP form's index-positional `defaultValues[index]`/`getFieldState`, so a
 * field-array remove/reorder can't make a shifted row inherit a sibling's state.
 *
 * When the tier's `residuaryPaysAllTaxes` flag is on, the residuary toggle owns
 * every recipient's `bearsTax`, so this hook stands down.
 */
export function useDefaultBdgRecipientBearsTax({
  tier,
  index,
}: {
  tier: 'primary' | 'contingent';
  index: number;
}) {
  const {
    setValue,
    control,
    formState: { isLoading },
  } = useFormContext<BeneficiaryDesignationsFormShape>();

  const bearsTaxFieldName =
    `${BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE}.${tier}.recipients.${index}.bearsTax` as const satisfies PathsOf<BeneficiaryDesignationsFormShape>;

  // Watch the whole array under a constant `name` and index in, rather than
  // watching `recipients.${index}` directly: a `useWatch` whose name embeds the
  // index lags a render behind after a reorder (the index prop updates before
  // the subscription re-reads), so the row would briefly read another row's
  // value. Reading from one array snapshot keeps the index consistent.
  const recipients: DispositiveProvisionsFormRecipient[] | undefined = useWatch(
    {
      control,
      name: `${BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE}.${tier}.recipients`,
    }
  );

  const residuaryPaysAllTaxes = useWatch({
    control,
    name: `${BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE}.${tier}.residuaryPaysAllTaxes`,
  });

  const recipient = recipients?.[index];
  const transferTaxKind = recipient?.transferTaxKind;
  const recipientBearsTax = recipient?.bearsTax;

  useEffect(() => {
    if (isLoading) return;
    // When the residuary pays all taxes, the per-tier toggle owns bearsTax.
    if (residuaryPaysAllTaxes) return;
    if (!transferTaxKind) return;
    // Default once: never override a value already set (saved record, manual
    // toggle, or the residuary toggle's flag-off write).
    if (recipientBearsTax !== undefined) return;

    setValue(bearsTaxFieldName, !isNonTaxable(transferTaxKind), {
      shouldDirty: false,
    });
  }, [
    bearsTaxFieldName,
    isLoading,
    recipientBearsTax,
    residuaryPaysAllTaxes,
    setValue,
    transferTaxKind,
  ]);
}
